/**
 * TeamBucketsSection: the buckets a team's data and traces live in, set by
 * staff (TRA-1705). One form over PUT /api/internal/teams/:id/buckets.
 *
 * The data buckets are one comma-separated field: the platform reads them
 * as one logical bucket and writes the first. Empty, the team uses the
 * stack's default bucket, shown beside the field. The trace bucket is one
 * name; empty, the deployment's trace bucket. The save writes the gateway
 * team's trace bucket first and the row second, so the API's 400 for a
 * gateway that cannot open buckets by name arrives before anything is
 * stored. A save writes the API's answer into the query cache.
 */

import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { customFetch, getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import {
  getGetTeamBucketsApiInternalTeamsTeamIdBucketsGetQueryKey,
  useGetTeamBucketsApiInternalTeamsTeamIdBucketsGet,
} from '@/api/generated/internal/internal'
import type { TeamBucketsResponse, UpdateTeamBucketsRequest } from '@/api/generated/model'
import { SettingsFormFooter } from '@/components/settings/SettingsFormFooter'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsBlock, SettingsSection } from '@/components/settings/SettingsSection'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

import type { TeamConfigSectionProps } from './sections'
import { fieldsOf, parseBuckets, type BucketsEdit } from './teamBuckets'

export function TeamBucketsSection({ section, team }: TeamConfigSectionProps): React.JSX.Element {
  const queryClient = useQueryClient()
  const query = useGetTeamBucketsApiInternalTeamsTeamIdBucketsGet(team.id, {
    query: { retry: false },
  })
  const saved = query.data?.status === 200 ? query.data.data : undefined
  const [edit, setEdit] = useState<BucketsEdit | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const saveMutation = useMutation({
    mutationFn: (body: UpdateTeamBucketsRequest) =>
      customFetch<TeamBucketsResponse>(`/api/internal/teams/${team.id}/buckets`, {
        method: 'PUT',
        data: body,
      }),
    onSuccess: (answer) => {
      queryClient.setQueryData(getGetTeamBucketsApiInternalTeamsTeamIdBucketsGetQueryKey(team.id), {
        status: 200,
        data: answer,
        headers: new Headers(),
      })
      setEdit(null)
      toast.success('Buckets saved')
    },
    onError: (error: unknown) => {
      const detail = getApiErrorDetail(error) ?? 'Failed to save the buckets'
      if (getApiErrorStatus(error) === 400) {
        setSaveError(detail)
      } else {
        toast.error(detail)
      }
    },
  })

  if (saved === undefined) {
    return (
      <div>
        <SettingsPageHeader section={section} />
        <SettingsBlock>
          {query.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {getApiErrorDetail(query.error) ?? 'Failed to load the buckets'}
            </p>
          ) : (
            <Skeleton className="h-24 w-full" />
          )}
        </SettingsBlock>
      </div>
    )
  }

  const fields = edit ?? fieldsOf(saved)
  const dataBuckets = parseBuckets(fields.dataBuckets)
  const traceBucket = fields.traceBucket.trim()
  const dirty =
    edit !== null &&
    (dataBuckets.join(',') !== saved.data_buckets.join(',') ||
      traceBucket !== (saved.trace_bucket ?? ''))
  const canSubmit = dirty && !saveMutation.isPending

  const update = (change: Partial<BucketsEdit>): void => {
    setEdit((previous) => ({ ...(previous ?? fieldsOf(saved)), ...change }))
    setSaveError(null)
  }
  const discard = (): void => {
    setEdit(null)
    setSaveError(null)
  }
  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (!canSubmit) return
    setSaveError(null)
    saveMutation.mutate({
      data_buckets: dataBuckets,
      trace_bucket: traceBucket === '' ? null : traceBucket,
    })
  }

  return (
    <div>
      <SettingsPageHeader section={section} />
      <form onSubmit={handleSubmit}>
        <SettingsSection
          title="Data buckets"
          description="The platform reads these buckets as one and writes the first. Leave empty to use the stack's bucket for this team."
          aside={
            <p className="text-xs text-muted-foreground">
              Team slug <span className="font-mono">{saved.slug}</span>
            </p>
          }
        >
          <div className="max-w-xl space-y-2">
            <Label htmlFor="team-data-buckets">Buckets</Label>
            <Input
              id="team-data-buckets"
              value={fields.dataBuckets}
              placeholder={saved.default_data_bucket ?? 'bkt-team-data, bkt-team-raw'}
              disabled={saveMutation.isPending}
              aria-describedby="team-data-buckets-help"
              onChange={(e) => {
                update({ dataBuckets: e.target.value })
              }}
            />
            <p id="team-data-buckets-help" className="text-xs text-muted-foreground">
              Comma-separated bucket names, the written one first. Saving moves no object: keep a
              bucket that holds this team's data in the list, or that data stops being read. Grant
              the api and the training identities the same access on each as on the stack's bucket.
              {saved.default_data_bucket !== null && (
                <>
                  {' '}
                  Default for this team: <span className="font-mono">{saved.default_data_bucket}</span>.
                </>
              )}
            </p>
          </div>
        </SettingsSection>

        <SettingsSection
          title="Trace bucket"
          description="The gateway writes this team's traces here. Leave empty to use the deployment's trace bucket."
        >
          <div className="max-w-xl space-y-2">
            <Label htmlFor="team-trace-bucket">Bucket</Label>
            <Input
              id="team-trace-bucket"
              value={fields.traceBucket}
              placeholder={saved.default_trace_bucket ?? 'bkt-team-data'}
              disabled={saveMutation.isPending}
              aria-describedby="team-trace-bucket-help"
              onChange={(e) => {
                update({ traceBucket: e.target.value })
              }}
            />
            <p id="team-trace-bucket-help" className="text-xs text-muted-foreground">
              Saving sets the gateway team's trace bucket too and moves no trace: copy the team's
              traces prefix into the new bucket first, or its earlier trajectories stop opening. A
              gateway with one trace store refuses a team bucket.
            </p>
          </div>
        </SettingsSection>

        <SettingsFormFooter dirty={dirty} error={saveError}>
          <Button
            type="button"
            variant="outline"
            disabled={edit === null || saveMutation.isPending}
            onClick={discard}
          >
            Discard
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            Save
          </Button>
        </SettingsFormFooter>
      </form>
    </div>
  )
}
